import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { friendlyDeviceTypeLabel, lastHeardText, OFFLINE_CONFIRM_MIN } from '../devices.js';
import { paramLabel } from '../../../shared/param-labels.js';
import { testTimeUnknown } from '../../../shared/trident-trust.js';
import { assessFreshnessClient } from '../utils.js';
import { getDisplayUnits, formatTemp } from '../units.js';
import { formatSalinity, salinityUnitLabel, readSalinityUnit } from '../salinity.js';
import { formatCardValue } from '../param-history.js';
import { probePickMessage } from '../probe-pick.js';
import { OPEN_ACCOUNT_EVENT } from '../app-open.js';
import { DevicesPanel, formatAgo } from './DevicesPanel.jsx';
import { HydrosLinkPanel } from './HydrosLinkPanel.jsx';
import { useConfirm } from './ConfirmDialog.jsx';
import { ringRowText } from './RingCamera.jsx';

// One home for devices (owner, 2026-10-21; UI step 4). Equipment and
// devices opens with a short Connected list: each PrintedReef display on
// the tank and a linked HYDROS controller, with its status, one tap to the
// device's own page. A keeper with neither sees one "Connect a device" row
// instead of two cards above their gear. Signing in to HYDROS and its keys
// stay in Account; anything a device can be told to do will live only on
// its own page (CLAUDE.md, "Don't crowd the app").

export function deviceStatus(device, now = Date.now()) {
  const seen = device?.last_seen_at ? new Date(device.last_seen_at).getTime() : NaN;
  if (!Number.isFinite(seen)) return { quiet: true, text: 'Not heard from yet' };
  if (now - seen >= OFFLINE_CONFIRM_MIN * 60_000) {
    return {
      quiet: true,
      text: `Not reporting since ${lastHeardText(device.last_seen_at, now)}`,
    };
  }
  return { quiet: false, text: `Reporting, last heard ${formatAgo(device.last_seen_at)}` };
}

function versionAtLeast(v, min) {
  const a = String(v || '').match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!a) return false;
  const b = min.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const x = Number(a[i + 1]);
    if (x !== b[i]) return x > b[i];
  }
  return true;
}

// How a keeper updates a display, from its own firmware: the 7-inch
// display's About & Updates works from 0.3.1 (earlier builds showed a
// placeholder and need a USB flash); the round display has UPDATE on its
// SETUP screen.
export function updateHint(device) {
  if (device?.device_type === 'desktop7' && versionAtLeast(device.fw_version, '0.3.1')) {
    return 'To update: on the display, Settings, About & Updates, Check for updates.';
  }
  if (device?.device_type === 'trident175') {
    return "To update: on the display's SETUP screen, tap UPDATE.";
  }
  return null;
}

// The Apex readings on this tank since the display was paired, newest per
// test. They are stored against the tank, not the display, so with two
// displays on one tank the page says they are the tank's. A Trident result
// carries no test time (shared/trident-trust.js), so it says so.
export function apexReadings(tank, { since = null } = {}) {
  const from = since ? new Date(since).getTime() : -Infinity;
  const byParam = new Map();
  for (const r of tank?.readings || []) {
    if (r.method !== 'apex' || r.value == null) continue;
    if (new Date(r.ts).getTime() < from) continue;
    if (!byParam.has(r.param)) byParam.set(r.param, []);
    byParam.get(r.param).push(r);
  }
  const out = [];
  for (const [param, rows] of byParam) {
    rows.sort((a, b) => new Date(b.ts) - new Date(a.ts));
    const fresh = assessFreshnessClient(rows, param);
    out.push({ ...rows[0], freshness: fresh, unknownTime: testTimeUnknown(rows[0], param) });
  }
  return out.sort((a, b) => new Date(b.ts) - new Date(a.ts));
}

// A reading in the keeper's own units, as Home shows it.
function shownValue(tank, r) {
  const units = getDisplayUnits(tank);
  if (r.param === 'salinity') {
    const u = readSalinityUnit();
    return `${formatSalinity(r.value, u)} ${salinityUnitLabel(u)}`.trim();
  }
  if (r.param === 'temp' && units.temp === 'C') return `${formatTemp(r.value, 'C')}`;
  const unit = tank?.params?.[r.param]?.unit || r.unit || '';
  return `${formatCardValue(r.param, r.value)}${unit ? ` ${unit}` : ''}`;
}

function readingWhen(r) {
  if (r.unknownTime) return `arrived ${formatAgo(r.ts)}, test time unknown`;
  if (r.freshness?.reason === 'stuck') return 'same value every update, probe likely stuck';
  if (r.freshness?.reason === 'frozen') return 'not moving in days';
  return formatAgo(r.ts);
}

export function ConnectedDevices({ tank, devices, error, onRetry, hydros, ring, onOpen }) {
  const isOwner = !tank.role || tank.role === 'owner';
  const hasHydros = !!tank.hydros_thing_id;
  const list = devices || [];
  if (error) {
    return (
      <div className="dvh-group" role="alert">
        <p className="ts-hint">Could not load this tank&apos;s devices.</p>
        <button type="button" className="ts-chip dvh-connect-more" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }
  if (devices === null) {
    return (
      <p className="ts-hint" style={{ marginBottom: 12 }}>
        Loading devices…
      </p>
    );
  }
  // A Ring camera row, for an owner in the Ring beta (RingCamera.jsx).
  const ringRow = ring?.available ? (
    <li>
      <button type="button" className="dvh-row" onClick={() => onOpen({ kind: 'ring' })}>
        <span className="dvh-row-text">
          <span className="dvh-row-title">{ring.camera?.name || 'Ring camera'}</span>
          <span className={`dvh-row-sub${ring.camera?.needs_relink ? ' dvh-row-sub--quiet' : ''}`}>
            {ringRowText(ring)}
          </span>
        </span>
        <Chevron />
      </button>
    </li>
  ) : null;
  if (!list.length && !hasHydros) {
    if (!isOwner) return null;
    return (
      <ul className="dvh-list" aria-label="Connected devices">
        {ring?.camera && ringRow}
        <li>
          <button type="button" className="dvh-row" onClick={() => onOpen({ kind: 'connect' })}>
            <span className="dvh-row-text">
              <span className="dvh-row-title">Connect a device</span>
              <span className="dvh-row-sub">
                An Apex through a PrintedReef display, or a HYDROS controller
              </span>
            </span>
            <Chevron />
          </button>
        </li>
        {!ring?.camera && ringRow}
      </ul>
    );
  }
  const hydrosStatus = hydrosLine(tank, hydros);
  return (
    <div className="dvh-group">
      <div className="dvh-group-label font-heading">Connected</div>
      <ul className="dvh-list" aria-label="Connected devices">
        {list.map((d) => {
          const st = deviceStatus(d);
          return (
            <li key={d.id}>
              <button
                type="button"
                className="dvh-row"
                onClick={() => onOpen({ kind: 'device', id: d.id })}
              >
                <span className="dvh-row-text">
                  <span className="dvh-row-title">
                    {d.display_name || friendlyDeviceTypeLabel(d.device_type, 'Display')}
                  </span>
                  <span className={`dvh-row-sub${st.quiet ? ' dvh-row-sub--quiet' : ''}`}>
                    {st.text}
                  </span>
                </span>
                <Chevron />
              </button>
            </li>
          );
        })}
        {hasHydros && (
          <li>
            <button type="button" className="dvh-row" onClick={() => onOpen({ kind: 'hydros' })}>
              <span className="dvh-row-text">
                <span className="dvh-row-title">HYDROS controller</span>
                <span className={`dvh-row-sub${hydrosStatus.warn ? ' dvh-row-sub--quiet' : ''}`}>
                  {hydrosStatus.text}
                </span>
              </span>
              <Chevron />
            </button>
          </li>
        )}
        {ringRow}
      </ul>
      {isOwner && (
        <button
          type="button"
          className="ts-chip dvh-connect-more"
          onClick={() => onOpen({ kind: 'connect' })}
        >
          Connect another device
        </button>
      )}
    </div>
  );
}

// The HYDROS row's one status line, from GET /tanks/:id/hydros: a stale
// controller and a pick still to make look different from a working one.
export function hydrosLine(tank, link) {
  if (!link) return { warn: false, text: 'Its probes, and which ones this tank reads' };
  if (link.stale) return { warn: true, text: 'Not reporting lately' };
  if (probePickMessage(link, tank?.name || 'this tank', tank?.params)) {
    return { warn: true, text: "Pick this tank's probes" };
  }
  return { warn: false, text: 'Reporting' };
}

// A display's own page: what it is, whether it is reporting, what reaches
// this tank through it, its software, and (for the owner) moving or
// removing it.
export function DeviceDetail({ tank, device, deviceCount, onBack, onRemoved }) {
  const confirm = useConfirm();
  const isOwner = !tank.role || tank.role === 'owner';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const st = deviceStatus(device);
  const readings = apexReadings(tank, { since: device.claimed_at });
  const name = device.display_name || friendlyDeviceTypeLabel(device.device_type, 'Display');
  const hint = updateHint(device);
  const newest = readings[0] ? new Date(readings[0].ts).getTime() : null;
  // Reporting, but the Apex it reads has gone quiet: the display's own
  // connection is fine and the Apex login or address is the likely cause.
  const apexQuiet = !st.quiet && newest != null && Date.now() - newest > 2 * 3600_000;

  const remove = async () => {
    // Unlinking frees the display from the whole account, not just this
    // tank (DevicesPanel says the same).
    const ok = await confirm({
      title: `Remove ${name} from your account?`,
      body: 'It stops showing your tanks and sending readings, and its not-reporting alerts stop. To use it again, pair it from its screen. To show another tank instead, move it in Account, TankSensei devices.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/devices/${device.id}`, { method: 'DELETE' });
      await onRemoved?.();
    } catch (e) {
      // Removed elsewhere (Account) while this page was open.
      if (e?.status === 404) {
        await onRemoved?.();
        return;
      }
      setErr(e.message || 'Could not remove it.');
      setBusy(false);
    }
  };

  return (
    <section className="ts-section eq-screen">
      <div className="eq-screen-head">
        <button type="button" className="eq-back" onClick={onBack}>
          ← Equipment
        </button>
        <h3 className="font-heading" style={{ margin: 0 }}>
          {name}
        </h3>
      </div>
      <dl className="dvh-facts">
        <div>
          <dt>Status</dt>
          <dd className={st.quiet ? 'dvh-quiet' : undefined}>
            {st.text}
            {st.quiet && device.last_seen_at ? '. Check its power and Wi-Fi.' : ''}
          </dd>
        </div>
        <div>
          <dt>Device</dt>
          <dd>{friendlyDeviceTypeLabel(device.device_type, 'PrintedReef display')}</dd>
        </div>
        {device.fw_version && (
          <div>
            <dt>Software</dt>
            <dd>
              {device.fw_version}
              {hint ? `. ${hint}` : ''}
            </dd>
          </div>
        )}
        {/* The code that proves you hold the display: the owner's alone. */}
        {isOwner && (
          <div>
            <dt>Pairing code</dt>
            <dd className="font-mono">{device.client_id}</dd>
          </div>
        )}
      </dl>
      <h4 className="font-heading dvh-sub-head">
        {deviceCount > 1 ? "This tank's Apex readings" : 'What it sends to this tank'}
      </h4>
      {apexQuiet && (
        <p className="ts-hint dvh-quiet">
          The display is reporting, but no Apex reading has arrived since{' '}
          {lastHeardText(readings[0].ts)}. Check the Apex address and login on the display.
        </p>
      )}
      {readings.length ? (
        <ul className="dvh-readings">
          {readings.map((r) => (
            <li key={r.param}>
              <span>
                {tank.params?.[r.param]?.label || paramLabel(r.param) || r.param}{' '}
                <span className="font-mono">{shownValue(tank, r)}</span>
              </span>
              <span
                className={`dvh-reading-age${r.unknownTime || r.freshness?.stale ? ' dvh-quiet' : ''}`}
              >
                {readingWhen(r)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="ts-hint">
          No Apex readings since it was paired. A display that only shows your tanks sends none; one
          reading an Apex sends its probes here.
        </p>
      )}
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
      {isOwner && (
        <div className="dvh-actions">
          <button
            type="button"
            className="ts-chip dvh-remove"
            onClick={() =>
              window.dispatchEvent(new CustomEvent(OPEN_ACCOUNT_EVENT, { detail: 'devices' }))
            }
          >
            Show another tank on it
          </button>
          <button
            type="button"
            className="ts-chip ts-chip-danger dvh-remove"
            onClick={remove}
            disabled={busy}
          >
            {busy ? 'Removing…' : 'Remove from my account'}
          </button>
        </div>
      )}
    </section>
  );
}

// HYDROS on this tank: the controller, its probes and which ones the tank
// reads (HydrosLinkPanel, unchanged).
export function HydrosDetail({ tank, onReload, onBack }) {
  return (
    <section className="ts-section eq-screen">
      <div className="eq-screen-head">
        <button type="button" className="eq-back" onClick={onBack}>
          ← Equipment
        </button>
        <h3 className="font-heading" style={{ margin: 0 }}>
          HYDROS controller
        </h3>
      </div>
      <HydrosLinkPanel tank={tank} onReload={onReload} />
    </section>
  );
}

// Connect a device: pair a PrintedReef display, or link a HYDROS controller
// from the keeper's connected HYDROS account (not repeated once one is
// linked: its own page has it). A display paired here goes back to the
// list, where it now shows.
export function ConnectDevice({ tank, onReload, onBack }) {
  return (
    <section className="ts-section eq-screen">
      <div className="eq-screen-head">
        <button type="button" className="eq-back" onClick={onBack}>
          ← Equipment
        </button>
        <h3 className="font-heading" style={{ margin: 0 }}>
          Connect a device
        </h3>
      </div>
      <DevicesPanel tank={tank} onReload={onReload} listed={false} onPaired={onBack} />
      {!tank.hydros_thing_id && <HydrosLinkPanel tank={tank} onReload={onReload} />}
    </section>
  );
}

// The tank's devices, asked again whenever the tank reloads (the shell's
// heartbeat) and when the app comes back into view: a page left open read a
// display that had come back as quiet, or a healthy one as quiet after the
// phone was locked.
export function useTankDevices(tank) {
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(false);
  const reload = async () => {
    try {
      const r = await api(`/api/tanks/${tank.id}/devices`);
      setDevices(r.devices || []);
      setError(false);
    } catch {
      setError(true);
    }
  };
  useEffect(() => {
    reload();
  }, [tank.id, tank]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [tank.id]);
  return [devices, reload, error];
}

// The HYDROS link's status for the Connected row, only when one is linked.
export function useHydrosLink(tank) {
  const [link, setLink] = useState(null);
  useEffect(() => {
    if (!tank.hydros_thing_id) {
      setLink(null);
      return undefined;
    }
    let live = true;
    api(`/api/tanks/${tank.id}/hydros`)
      .then((l) => live && setLink(l))
      .catch(() => live && setLink(null));
    return () => {
      live = false;
    };
  }, [tank.id, tank.hydros_thing_id]);
  return link;
}

function Chevron() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--text-dim)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
